import { DefaultContext, FetchResult, MutationHookOptions, MutationResult, NoInfer, useMutation } from '@apollo/client';
import { createError } from '@common/neo.tax';
import * as Sentry from '@sentry/react';
import merge from 'lodash/merge';

import {
  apolloClient,
  UpdateRdProjectInput,
  UpdateRdProjectMutation,
  UpdateRdProjectMutationVariables,
  UPDATE_RD_PROJECT,
  RdProjectFragment,
  RdProjectFragmentDocument,
  GetRdExpenseProjectAllocationsQuery,
  updateWithInputObject,
} from '@neo.tax/shared';

import { RdProject } from './useRdProjects';

type UseUpdateRdProjectProps<TContext = DefaultContext> = {
  context?: TContext;
  rdProject: RdProject;
  /**
   * Only include the optimistic response when we are NOT doing real-time updates. Why?
   * Real-time updates often exhibit a delay when including the optimistic response, and we don't
   * want a janky UI/UX, so opt in when it makes the most sense.
   */
  includeOptimisticResponse?: boolean;
};

interface UpdateProjectOptions extends MutationHookOptions<
  NoInfer<UpdateRdProjectMutation>,
  NoInfer<UpdateRdProjectMutationVariables>,
  DefaultContext
> {
  /**
   * Only include the optimistic response when we are NOT doing real-time updates. Why?
   * Real-time updates often exhibit a delay when including the optimistic response, and we don't
   * want a janky UI/UX, so opt in when it makes the most sense.
   */
  includeOptimisticResponse?: boolean;
}

type UseUpdateRdProject = [
  (
    updateRdProjectInput: UpdateRdProjectInput,
    options?: UpdateProjectOptions,
  ) => Promise<FetchResult<UpdateRdProjectMutation>>,
  MutationResult<UpdateRdProjectMutation>,
];

export const useUpdateRdProject = ({
  context: contextFromInit,
  includeOptimisticResponse: includeOptimisticResponseFromInit,
  rdProject,
}: UseUpdateRdProjectProps): UseUpdateRdProject => {
  const [updateRdProjectMutation, updateRdProjectMutationResult] = useMutation<
    UpdateRdProjectMutation,
    UpdateRdProjectMutationVariables
  >(UPDATE_RD_PROJECT, {
    context: {
      successToast: {
        title: 'Project updated successfully',
      },
      errorToast: {
        title: 'Error updating project',
        description: 'Something unexpected prevented your changes from saving. Please try again.',
      },
      ...contextFromInit,
    },
  });

  const updateRdProject = (
    updateRdProjectInput: UpdateRdProjectInput,
    options: UpdateProjectOptions = {},
  ): Promise<FetchResult<UpdateRdProjectMutation>> => {
    const { includeOptimisticResponse, ...otherOptions } = merge(
      {},
      {
        context: contextFromInit,
        includeOptimisticResponse: includeOptimisticResponseFromInit,
      },
      options,
    );

    let optimisticResponse: UpdateRdProjectMutation | undefined;

    if (includeOptimisticResponse) {
      const rdProjectFromCache = apolloClient.readFragment<RdProjectFragment>({
        id: `RdProject:${rdProject.id}`,
        fragment: RdProjectFragmentDocument,
        fragmentName: 'RdProject',
      });
      optimisticResponse = rdProjectFromCache
        ? {
            __typename: 'Mutation',
            updateRdProject: updateWithInputObject(rdProjectFromCache, updateRdProjectInput),
          }
        : undefined;
    }

    return updateRdProjectMutation({
      variables: {
        rdProjectId: rdProject.id,
        updateRdProjectInput,
      },
      optimisticResponse,
      onCompleted: (data) => {
        // Update the cache after successful mutation
        if (data?.updateRdProject) {
          try {
            // Update the AllocationProjectDetails cache entry directly
            const cache = updateRdProjectMutationResult.client.cache;

            cache.modify({
              id: `AllocationProjectDetails:${rdProject.id}`,
              fields: {
                name: () => data.updateRdProject.name,
                consideredForRd: () => data.updateRdProject.consideredForRd,
                isSection174Eligible: () => data.updateRdProject.isSection174Eligible,
              },
            });

            // Update the RdExpenseProjectAllocation entries
            cache.modify({
              fields: {
                getRdExpenseProjectAllocations: (existingData, { readField }) => {
                  if (!existingData?.edges) {
                    return existingData;
                  }

                  const updatedEdges = existingData.edges.map(
                    (edge: GetRdExpenseProjectAllocationsQuery['getRdExpenseProjectAllocations']['edges'][number]) => {
                      const projectId = readField('rdProjectId', edge.node);
                      if (projectId === rdProject.id) {
                        return {
                          ...edge,
                          node: {
                            ...edge.node,
                            projectName: data.updateRdProject.name,
                            project: {
                              ...edge.node.project,
                              name: data.updateRdProject.name,
                              consideredForRd: data.updateRdProject.consideredForRd,
                              isSection174Eligible: data.updateRdProject.isSection174Eligible,
                            },
                          },
                        };
                      }
                      return edge;
                    },
                  );

                  return {
                    ...existingData,
                    edges: updatedEdges,
                  };
                },
              },
            });
          } catch (error) {
            Sentry.captureException(
              createError(
                'UpdateRdProjectAllocationsCacheError',
                `Failed to update cache for project ${rdProject.id}. ${error}`,
              ),
            );
          }
        }
      },
      ...otherOptions,
    });
  };

  return [updateRdProject, updateRdProjectMutationResult];
};
